import type { DeliveryStatus, PaperCountability, PaperListRow } from "../api/types";
import { canSchedule, isBill, isCreditNote } from "./paperUse";

/**
 * G-131 — what one row of the Supplier papers page shows beside its name.
 * Words only: every fact comes from the server's row (`waitingForFactura`,
 * `coveredByFactura`, `covers`, `delivery`), so no rule is restated here.
 */
export type PaperPill = { key: string; text: string; tone: "warn" | "ok" | "muted" | "accent" };

const DELIVERY_WORDS: Record<DeliveryStatus, string> = {
  SCHEDULED: "Delivery scheduled",
  IN_PROGRESS: "Delivery being received",
  COMPLETED: "Delivery received",
  CANCELED: "Delivery cancelled",
};

/** G-164: what a read factura lacks for the Finances card to count it — short, and as the pill says it. */
const NOT_COUNTED: Partial<Record<PaperCountability, { reason: string; pill: string }>> = {
  NO_DATE_OR_NUMBER: { reason: "No date or number", pill: "Not counted: no date or number" },
  IVA_NOT_READ: { reason: "IVA not read", pill: "Not counted: IVA not read" },
  // G-173: the sign does not match the type — re-file the factura, or type the credit note's amounts.
  CREDIT_NOTE: { reason: "Negative amounts", pill: "Not counted: negative amounts" },
  CREDIT_NOTE_NOT_NEGATIVE: { reason: "Credit note above zero", pill: "Not counted: credit note above zero" },
};

/** The short reason ("No date or number"); null when the paper counts or is not a factura. */
export function notCountedReason(countability: PaperCountability | undefined): string | null {
  return countability ? (NOT_COUNTED[countability]?.reason ?? null) : null;
}

/** The pill a read factura carries when the Finances card cannot count it; null otherwise. */
export function notCountedText(countability: PaperCountability | undefined): string | null {
  return countability ? (NOT_COUNTED[countability]?.pill ?? null) : null;
}

export function paperPills(row: PaperListRow): PaperPill[] {
  const pills: PaperPill[] = [];
  if (row.status === "UPLOADED" || row.status === "PARSING") {
    pills.push({ key: "read", text: "Being read", tone: "muted" });
  } else if (row.status === "FAILED") {
    pills.push({ key: "read", text: "Could not be read", tone: "warn" });
  } else if (row.status === "NEEDS_REVIEW") {
    pills.push({ key: "read", text: "Needs review", tone: "warn" });
  }

  if (row.delivery) {
    pills.push({
      key: "delivery",
      text: DELIVERY_WORDS[row.delivery.status],
      tone: row.delivery.status === "COMPLETED" ? "ok" : "muted",
    });
  } else if (canSchedule(row.paperUse) && (row.status === "PARSED" || row.status === "NEEDS_REVIEW")) {
    pills.push({ key: "delivery", text: "No delivery yet", tone: "muted" });
  }

  // G-164: a factura the Finances card cannot count, and why — the server's rule.
  const notCounted = notCountedText(row.countability);
  if (notCounted) pills.push({ key: "counted", text: notCounted, tone: "warn" });

  // G-173: a credit note names the factura it corrects; a factura how many credit notes it has.
  if (isCreditNote(row.paperUse) && row.status !== "UPLOADED" && row.status !== "PARSING" && row.status !== "FAILED") {
    pills.push(
      row.correctsFactura
        ? { key: "credit", text: `Corrects factura ${row.correctsFactura.label}`, tone: "ok" }
        : row.correctsElsewhere
          ? { key: "credit", text: "Linked to a factura of another shop", tone: "ok" }
          : { key: "credit", text: "Not linked to a factura", tone: "warn" },
    );
  } else if (isBill(row.paperUse) && row.creditNotes > 0) {
    pills.push({
      key: "credit",
      text: row.creditNotes === 1 ? "1 credit note" : `${row.creditNotes} credit notes`,
      tone: "accent",
    });
  }

  if (row.waitingForFactura) {
    pills.push({ key: "factura", text: "Waiting for a factura", tone: "warn" });
  } else if (row.coveredByFactura) {
    pills.push({ key: "factura", text: `Counted by factura ${row.coveredByFactura.label}`, tone: "ok" });
  } else if (isBill(row.paperUse) && row.covers > 0) {
    pills.push({
      key: "factura",
      text: row.covers === 1 ? "Covers 1 albarán or delivery" : `Covers ${row.covers} albaranes or deliveries`,
      tone: "accent",
    });
  }
  return pills;
}

/** The figure a card leads with: the printed total, else the printed base, else nothing. */
export function paperHeadline(row: PaperListRow): { amount: string; label: string } | null {
  if (row.grandTotalEur !== null) return { amount: row.grandTotalEur, label: "with IVA" };
  if (row.baseEur !== null) return { amount: row.baseEur, label: "before IVA" };
  return null;
}

/** The last `count` business months, newest first, as "YYYY-MM". */
export function recentMonths(today: { year: number; month: number }, count: number): string[] {
  const out: string[] = [];
  let year = today.year;
  let month = today.month;
  for (let i = 0; i < count; i += 1) {
    out.push(`${year}-${String(month).padStart(2, "0")}`);
    month -= 1;
    if (month === 0) {
      month = 12;
      year -= 1;
    }
  }
  return out;
}
